import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Maximize2, MessageCircle, BedDouble } from "lucide-react";
import { formatPrice, whatsappLink, PROPERTY_TYPES } from "@/lib/api";

export default function PropertyCard({ p }) {
  const typeLabel = PROPERTY_TYPES.find((t) => t.value === p.property_type)?.label || p.property_type;
  const img = (p.images && p.images[0]) || "https://images.unsplash.com/photo-1773470920361-4f6cbb702ce0?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";
  const waMsg = `Hello Apurba Estate, I am interested in listing ${p.listing_id} — ${p.title} (${p.location}). Please share more details.`;

  return (
    <div className="ae-card overflow-hidden flex flex-col" data-testid={`property-card-${p.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-[#F4F6F8]">
        <img src={img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
        {p.is_demo && (
          <div className="absolute top-3 left-3 ae-chip bg-[#101827]/85 !border-transparent text-white">Demo Listing</div>
        )}
        <div className="absolute top-3 right-3 ae-chip bg-white/95 text-[#101827] !border-transparent">{p.listing_id}</div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between text-xs text-[#64748B] mb-2">
          <span className="uppercase tracking-wider font-semibold text-[#8E7340]">{typeLabel}</span>
          <span>{p.district}</span>
        </div>
        <h3 className="font-bold text-[#101827] text-lg leading-snug mb-2 line-clamp-2">{p.title}</h3>
        <div className="flex items-center gap-1.5 text-sm text-[#64748B] mb-4">
          <MapPin className="w-3.5 h-3.5" /> {p.location}
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs text-[#1F2937] mb-5">
          <div className="flex flex-col">
            <span className="text-[#64748B]">Area</span>
            <span className="font-semibold">{p.area_value} {p.area_unit}</span>
          </div>
          {p.bedrooms ? (
            <div className="flex flex-col">
              <span className="text-[#64748B]">Beds</span>
              <span className="font-semibold flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> {p.bedrooms}</span>
            </div>
          ) : (
            <div className="flex flex-col">
              <span className="text-[#64748B]">Facing</span>
              <span className="font-semibold">{p.facing || "—"}</span>
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-[#64748B]">Status</span>
            <span className="font-semibold text-[#059669]">Available</span>
          </div>
        </div>

        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#64748B]">{p.price_label || "Asking Price"}</div>
            <div className="font-bold text-[#183B66] text-xl">{formatPrice(p.price)}</div>
          </div>
        </div>

        <div className="flex gap-2 mt-auto">
          <Link
            to={`/properties/${p.id}`}
            data-testid={`property-view-btn-${p.id}`}
            className="ae-btn-primary flex-1 justify-center !py-2.5 text-sm"
          >
            <Maximize2 className="w-4 h-4" /> View Details
          </Link>
          <a
            href={whatsappLink(waMsg)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`property-whatsapp-btn-${p.id}`}
            className="ae-btn-whatsapp !py-2.5"
            aria-label="WhatsApp"
          >
            <MessageCircle className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
